import { Link } from "@inertiajs/react"

import EngagementMetrics from "@/components/engagement-metrics"
import PlatformIcon from "@/components/icons/platform-icon"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { formatDate } from "@/lib/format"
import {
  authorizeConnectedAccountsPath,
  storySyndicationRepliesPath,
} from "@/routes"
import type { Platform, Syndication } from "@/types"

const CARD_CLASSES: Record<string, string> = {
  failed:
    "border-red-200 bg-red-50/80 dark:border-red-900/40 dark:bg-red-950/20",
  in_progress:
    "border-yellow-200 bg-yellow-50/80 dark:border-yellow-900/40 dark:bg-yellow-950/20",
  queued:
    "border-yellow-200 bg-yellow-50/80 dark:border-yellow-900/40 dark:bg-yellow-950/20",
  ready:
    "border-blue-200 bg-blue-50/80 dark:border-blue-900/40 dark:bg-blue-950/20",
  success:
    "border-green-200 bg-green-50/80 dark:border-green-900/40 dark:bg-green-950/20",
}

function detailLinesForCard(
  platform: Platform,
  syndication: Syndication,
  isPublishing: boolean,
  publishAt: string | null,
): { primary: string | null; secondary: string | null } {
  const viaPullRequest = platform.publishes_via_pull_request

  if (isPublishing) {
    return {
      primary: viaPullRequest
        ? "Opening a pull request now."
        : `Sending to ${platform.label} now.`,
      secondary: "This may take a moment.",
    }
  }

  if (syndication.status === "ready") {
    if (publishAt) {
      return {
        primary: `Scheduled for ${formatDate(publishAt, true)}.`,
        secondary: null,
      }
    }
    return { primary: null, secondary: null }
  }

  if (syndication.status === "failed") {
    return {
      primary: null,
      secondary:
        syndication.error_message ??
        "Try again when you're ready to republish.",
    }
  }

  if (syndication.status === "in_progress" || syndication.status === "queued") {
    return {
      primary: viaPullRequest
        ? "Opening a pull request now."
        : `Sending to ${platform.label} now.`,
      secondary: "This may take a moment.",
    }
  }

  const sentAt = syndication.published_at ?? syndication.created_at

  return {
    primary: viaPullRequest
      ? `PR opened ${formatDate(sentAt, true)}.`
      : `Sent ${formatDate(sentAt, true)}.`,
    secondary: null,
  }
}

interface Props {
  storyId: number
  publishAt: string | null
  publishable: boolean
  publishErrors: string[]
  publishing: Set<number>
  subscribed: boolean
  syndications: Syndication[]
  retryErrors: Record<number, string>
  onRetry: (syndication: Syndication) => void | Promise<void>
}

export default function SyndicationCards({
  storyId,
  publishAt,
  publishable,
  publishErrors,
  publishing,
  subscribed,
  syndications,
  retryErrors,
  onRetry,
}: Props) {
  if (syndications.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
        Syndication
      </p>
      {!publishable && (
        <div
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-100"
        >
          <p className="font-medium">This story isn&apos;t ready to publish.</p>
          {publishErrors.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
              {publishErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex flex-col gap-3">
        {syndications.map((syndication) => {
          const platform = syndication.platform
          const isPublishing = publishing.has(syndication.id)
          const viaPullRequest = platform.publishes_via_pull_request
          const isFailed = syndication.status === "failed"
          const effectiveStatus = isPublishing
            ? "in_progress"
            : syndication.status
          const details = detailLinesForCard(
            platform,
            syndication,
            isPublishing,
            publishAt,
          )
          const cardClass = CARD_CLASSES[effectiveStatus] ?? "border bg-card"
          const statusLabel = isPublishing
            ? "Publishing"
            : syndication.status_label
          const retryError = retryErrors[syndication.id]
          const showSuccessMetrics =
            syndication.status === "success" &&
            platform.supports_engagement_metrics
          const hasBodyContent =
            details.primary !== null ||
            details.secondary !== null ||
            showSuccessMetrics ||
            Boolean(retryError) ||
            isFailed ||
            Boolean(syndication.published_url)

          return (
            <section
              key={syndication.id}
              className={`rounded-xl border p-4 shadow-sm ${cardClass}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="bg-muted text-foreground flex size-10 shrink-0 items-center justify-center rounded-full">
                  <PlatformIcon platform={platform.name} className="size-5" />
                </div>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    {syndication.avatar_url && (
                      <Avatar className="size-5">
                        <AvatarImage src={syndication.avatar_url} alt="" />
                        <AvatarFallback />
                      </Avatar>
                    )}
                    <span className="truncate">
                      {syndication.display_label ?? platform.label}
                    </span>
                  </p>
                  <p className="text-muted-foreground text-xs">{statusLabel}</p>
                </div>
              </div>

              {hasBodyContent && (
                <div className="mt-4 flex flex-col gap-3">
                  <div className="space-y-1">
                    {details.primary && (
                      <p className="text-muted-foreground text-sm">
                        {details.primary}
                      </p>
                    )}
                    {details.secondary && (
                      <p
                        className={
                          isFailed
                            ? "text-sm text-red-600"
                            : "text-muted-foreground text-sm"
                        }
                      >
                        {details.secondary}
                      </p>
                    )}
                    {showSuccessMetrics && (
                      <p className="text-muted-foreground flex items-center gap-3 text-sm">
                        <EngagementMetrics
                          likes={syndication.likes_count}
                          views={syndication.views_count}
                          reposts={syndication.reposts_count}
                          shares={syndication.shares_count}
                          quotes={syndication.quotes_count}
                        />
                        {syndication.reply_count > 0 ? (
                          <Link
                            href={storySyndicationRepliesPath(
                              storyId,
                              syndication.id,
                            )}
                            className="underline underline-offset-4"
                          >
                            {syndication.reply_count}{" "}
                            {syndication.reply_count === 1
                              ? "reply"
                              : "replies"}
                          </Link>
                        ) : (
                          <span>No replies yet</span>
                        )}
                      </p>
                    )}
                    {retryError && (
                      <p className="text-sm text-red-600">{retryError}</p>
                    )}
                  </div>

                  {(isFailed || syndication.published_url) && (
                    <div className="flex flex-wrap gap-2">
                      {isFailed && syndication.requires_reconnect && (
                        <Button size="sm" asChild>
                          <a
                            href={authorizeConnectedAccountsPath(
                              syndication.platform.name,
                            )}
                          >
                            Reconnect {syndication.platform.label}
                          </a>
                        </Button>
                      )}
                      {isFailed && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!subscribed || isPublishing || !publishable}
                          onClick={() => void onRetry(syndication)}
                        >
                          Retry
                        </Button>
                      )}
                      {syndication.published_url && (
                        <a
                          href={syndication.published_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex h-9 items-center text-sm underline underline-offset-4"
                        >
                          {viaPullRequest ? "View PR" : "View post"}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
}
